import { useState, useEffect } from "react";
import Content from "./components/content/Content";
import Api from "./services/Api";

// Componente raiz da aplicação — gerencia a página ativa e injeta fontes/estilos globais
function App() {
  // Estado do usuário autenticado — restaurado do localStorage ao recarregar
  const [user, setUser] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("user")) || null;
      if (stored?.token) Api.setToken(stored.token);
      return stored;
    } catch {
      return null;
    }
  });
  const [page, setPage] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("page");
    return p || "search";
  });
  const [sessionExpired, setSessionExpired] = useState(false);

  // Falecido selecionado na SearchPage para exibir na MemorialPage
  const [selectedFalecido, setSelectedFalecido] = useState(null);
  
  // Jazigo selecionado na SearchPage para exibir na MemorialPage
  const [falecidos, setFalecidos] = useState([]);
    
  useEffect(() => {
    const func = async () => {
      const params = new URLSearchParams(window.location.search);
      const pageParam = params.get("page");
      const falecidoParam = params.get("selectedFalecido");
      const jazigoParam = params.get("selectedJazigo");

      if (falecidoParam) {
        const falecido = await Api.getFalecidosPorId(Number(falecidoParam));
        if (falecido) setSelectedFalecido(falecido);
      }

      if (jazigoParam) {
        const falecidos = await Api.getJazigoFalecidosAtivos(Number(jazigoParam));
        if (falecidos) setFalecidos(falecidos);
      }

      if (pageParam) setPage(pageParam);
    };

    func();
  }, []);

  // Persiste o usuário no localStorage e atualiza o token no singleton da API
  const handleLogin = (userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    if (userData?.token) Api.setToken(userData.token);
    setUser(userData);
  };

  // Limpa o token do singleton, remove do localStorage e redireciona para login
  const handleLogout = () => {
    localStorage.removeItem("user");
    Api.clearToken();
    setUser(null);
    setPage("login");
  };

  // Registra o logout automático quando o token expirar (401)
  Api.setOnUnauthorized(() => {
    setSessionExpired(true);
    localStorage.removeItem("user");
    Api.clearToken();
    setUser(null);
  });

  // Injeta as fontes do Google e os estilos globais de reset no carregamento
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Reset global de margens, paddings e estilos base
    const style = document.createElement("style");
    style.textContent = `
      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'DM Sans', sans-serif; background: #fafafa; }
      ::selection { background: #F2A66C; color: #fff; }
      input::placeholder { color: #bbb; }
      select:focus { border-color: #E8956A; }
      .leaflet-top.leaflet-right { position: fixed !important; top: 70px !important; right: 10px !important; z-index: 199 !important; }
    `;
    document.head.appendChild(style);
  }, []);

  // Função de navegação — altera a página ativa
  const navigate = (p, state = {}) => {
    if (state.selectedFalecido !== undefined) setSelectedFalecido(state.selectedFalecido);
    setPage(p);
  };

  // Re-busca o falecido selecionado se ele foi corrigido
  const handleFalecidoUpdated = async (falecidoIds) => {
    if (!selectedFalecido || !falecidoIds.includes(selectedFalecido.id)) return;
    try {
      const updated = await Api.getFalecidosPorId(selectedFalecido.id);
      if (updated) setSelectedFalecido(updated);
    } catch {}
  };

  return (
    <>
      {sessionExpired && (
        <div
          style={{
            position: "fixed",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            padding: "16px 28px",
            display: "flex",
            alignItems: "center",
            gap: 16,
            zIndex: 9999,
            minWidth: 320,
            borderLeft: "4px solid #E8956A",
          }}
        >
          <span
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 15,
              color: "#2d2d2d",
              flex: 1,
            }}
          >
            Sua sessão expirou. Faça login novamente.
          </span>
          <button
            onClick={() => setSessionExpired(false)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 18,
              color: "#aaa",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      )}
      <Content
        page={page}
        navigate={navigate}
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        selectedFalecido={selectedFalecido}
        onSelectFalecido={setSelectedFalecido}
        onFalecidoUpdated={handleFalecidoUpdated}
        falecidos={falecidos}
      />
    </>
  );
}

export default App;
